import React, { useState } from 'react';
import { enviarLeadDoSite } from '../../services/platform';
import './CustomSolutions.css';
import usePageMeta from '../../hooks/usePageMeta';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  description: string;
  budget: string;
}

const CustomSolutions: React.FC = () => {
  usePageMeta({
    title: 'Desenvolvimento Sob Medida',
    description:
      'Conte seu projeto para a UpdateCommit: desenvolvemos sistemas, integrações e automações sob medida para o seu negócio.',
    path: '/custom-solutions',
  });

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    description: '',
    budget: '',
  });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  // Isca anti-bot: fica escondido e fora da ordem de tabulação, então
  // pessoa nenhuma preenche. Bot que preenche todos os campos se entrega.
  const [honeypot, setHoneypot] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    try {
      // Vai para a plataforma operacional, não para o backend deste site:
      // é lá que os leads são acompanhados. Os UTM capturados na chegada
      // são anexados pelo próprio serviço.
      await enviarLeadDoSite(
        { ...formData, website_confirm: honeypot },
        { source: 'site', origin: 'custom-solutions' }
      );
      setStatus('success');
      setFormData({ name: '', email: '', phone: '', company: '', description: '', budget: '' });
    } catch (error) {
      setStatus('error');
      setErrorMsg('Erro ao enviar. Tente novamente ou nos chame no WhatsApp.');
    }
  };

  const isFormValid = formData.name && formData.email && formData.phone && formData.description;

  return (
    <div className="cs-page">
      {/* Hero */}
      <div className="cs-hero">
        <div className="cs-container">
          <span className="cs-badge">🛠️ Sob Medida</span>
          <h1 className="cs-title">
            Desenvolvimento <span className="cs-highlight">Custom</span>
          </h1>
          <p className="cs-subtitle">
            Soluções exclusivas criadas do zero para o seu negócio.
            Conte seu projeto e vamos construir juntos.
          </p>
        </div>
      </div>

      {/* Main */}
      <div className="cs-main">
        <div className="cs-container cs-grid">
          {/* Diferenciais */}
          <div className="cs-info">
            <h2 className="cs-info-title">Por que desenvolvimento custom?</h2>

            <div className="cs-feature">
              <div className="cs-feature-icon">🎯</div>
              <div>
                <h3>100% Personalizado</h3>
                <p>Cada linha de código pensada para o seu negócio, sem templates genéricos.</p>
              </div>
            </div>

            <div className="cs-feature">
              <div className="cs-feature-icon">⚡</div>
              <div>
                <h3>Escalável</h3>
                <p>Arquitetura pensada para crescer junto com sua empresa.</p>
              </div>
            </div>

            <div className="cs-feature">
              <div className="cs-feature-icon">🔒</div>
              <div>
                <h3>Seguro e confiável</h3>
                <p>Código limpo, documentado e com boas práticas de segurança.</p>
              </div>
            </div>

            <div className="cs-feature">
              <div className="cs-feature-icon">🤝</div>
              <div>
                <h3>Parceria real</h3>
                <p>Acompanhamento em todo o processo, do briefing ao deploy.</p>
              </div>
            </div>

            <div className="cs-whatsapp-alt">
              <p>Prefere falar direto?</p>
              <a
                href="https://wa.me/5521936185311?text=Olá!%20Tenho%20interesse%20em%20Desenvolvimento%20Custom."
                target="_blank"
                rel="noopener noreferrer"
                className="cs-btn-whatsapp"
              >
                💬 Chamar no WhatsApp
              </a>
            </div>
          </div>

          {/* Formulário */}
          <div className="cs-form-card">
            <h2 className="cs-form-title">Conte sobre seu projeto</h2>
            <p className="cs-form-subtitle">
              Preencha o formulário e entraremos em contato em até 24h.
            </p>

            {status === 'success' ? (
              <div className="cs-success">
                <div className="cs-success-icon">✅</div>
                <h3>Solicitação enviada!</h3>
                <p>Recebemos seu projeto e entraremos em contato em breve.</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="cs-btn-secondary"
                >
                  Enviar outro projeto
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="cs-form">
                {/* Isca anti-bot. aria-hidden e tabIndex -1 mantêm o campo
                    fora do leitor de tela e da navegação por teclado, então
                    ele não atrapalha quem usa o formulário de verdade. */}
                <input
                  type="text"
                  name="website_confirm"
                  className="cs-honeypot"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />

                <div className="cs-row">
                  <div className="cs-field">
                    <label htmlFor="cs-name">Nome *</label>
                    <input
                      id="cs-name"
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Seu nome completo"
                      required
                    />
                  </div>
                  <div className="cs-field">
                    <label htmlFor="cs-email">E-mail *</label>
                    <input
                      id="cs-email"
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="seu@email.com"
                      required
                    />
                  </div>
                </div>

                <div className="cs-row">
                  <div className="cs-field">
                    <label htmlFor="cs-phone">Telefone / WhatsApp *</label>
                    <input
                      id="cs-phone"
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="(21) 99999-9999"
                      required
                    />
                  </div>
                  <div className="cs-field">
                    <label htmlFor="cs-company">Empresa</label>
                    <input
                      id="cs-company"
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Nome da empresa"
                    />
                  </div>
                </div>

                <div className="cs-field">
                  <label htmlFor="cs-budget">Orçamento estimado</label>
                  <select
                    id="cs-budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                  >
                    <option value="">Selecione uma faixa</option>
                    <option value="Até R$ 5.000">Até R$ 5.000</option>
                    <option value="R$ 5.000 - R$ 15.000">R$ 5.000 – R$ 15.000</option>
                    <option value="R$ 15.000 - R$ 50.000">R$ 15.000 – R$ 50.000</option>
                    <option value="Acima de R$ 50.000">Acima de R$ 50.000</option>
                    <option value="A definir">A definir</option>
                  </select>
                </div>

                <div className="cs-field">
                  <label htmlFor="cs-description">Descrição do projeto *</label>
                  <textarea
                    id="cs-description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Descreva seu projeto: funcionalidades desejadas, prazo, tecnologias de preferência, etc."
                    rows={5}
                    required
                  />
                </div>

                {status === 'error' && (
                  <div className="cs-error">{errorMsg}</div>
                )}

                <button
                  type="submit"
                  className="cs-btn-primary"
                  disabled={!isFormValid || status === 'loading'}
                >
                  {status === 'loading' ? 'Enviando...' : '🚀 Enviar Solicitação'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomSolutions;
